import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  TextInputContentSizeChangeEventData,
  TextInputKeyPressEventData,
  View,
} from "react-native";
import { MicrophoneIcon } from "@phosphor-icons/react";
import { Send, Square, X } from "lucide-react";
import { AnimatePresence, MotiView } from "moti";
import { useTranslation } from "react-i18next";
import { AttachmentChip } from "../../interfaceComponents/attachmentChip/AttachmentChip";
import useResponsiveScreen from "../../../../hooks/useResponsiveScreen";
import { useAccessibilityContext } from "../../../../contexts/AccessibilityContext";
import { useMathVirtualKeyboardContext } from "../../../../contexts/MathVirtualKeyboardContext";
import ToolsMenu from "../../interfaceComponents/ToolsMenu/ToolsMenu";
import { VoiceMenu } from "../../interfaceComponents/ToolsMenu/VoiceMenu";
import { type ChatToolContext, type ToolId, type ToolOption } from "../../interfaceComponents/ToolsMenu/toolOptions";
import { type PlaybackSpeed } from "../../../../utils/toolPreferenceStorage";
import { AddToMessage } from "../../interfaceComponents/addToMessage/AddToMessage";
import Tooltip from "../../../common/tooltip/Tooltip";
import {
  colors,
  radii,
  spacing,
  typographyScale,
  typographyStyles,
  webTransitions,
  semanticColors,
} from "../../../../styles/design-system";
import { type ChatSpeechInputPhase } from "../../../../hooks/useChatSpeechInput";
import CustomText from "../../../common/general/CustomText/CustomText";
import MathFieldInput, { type MathFieldInputHandle } from "./MathFieldInput";
import MathKeyboardPalette from "./MathKeyboardPalette";

export interface ComposerAttachment {
  id: string;
  type: "task" | "assignment" | "file";
  name: string;
  duedate?: string | number | Date;
  isLoading?: boolean;
}

export interface ChatComposerBarProps {
  value: string;
  voiceInputPhase: ChatSpeechInputPhase;
  voiceInputErrorMessage?: string | null;
  audioLevel?: number;
  isAudioOn: boolean;
  placeholder: string;
  inputAccessibilityLabel?: string;
  inputRef?: React.RefObject<TextInput>;
  onChangeText: (text: string) => void;
  onSubmitEditing: (message: string) => void;
  isDisabled: boolean;
  onRecordingStartPress: () => void;
  onRecordingCancelPress: () => void;
  onRecordingStopPress: () => void;
  onAudioOnPress: () => void;
  onAudioOffPress: () => void;
  chatContext: ChatToolContext;
  tools: ToolOption[];
  activeToolId: ToolId | null;
  onToolSelect: (toolId: ToolId) => void;
  mathModeEnabled: boolean;
  onMathModeToggle: (enabled: boolean) => void;
  playbackSpeed: PlaybackSpeed;
  onPlaybackSpeedChange: (speed: PlaybackSpeed) => void;
  isAttachmentEnabled: boolean;
  attachment: ComposerAttachment | null;
  isAttachmentLoading: boolean;
  onAttachmentPress: () => void;
  onAddTaskPress?: () => void;
  onAddAssignmentPress?: () => void;
  onDeleteAttachmentPress: () => void;
  suggestedPrompts?: { text: string }[];
  onSuggestionPress?: (text: string) => void;
}

const INPUT_MIN_HEIGHT = 55;
const INPUT_MAX_HEIGHT = 160;
const INPUT_VERTICAL_PADDING = (INPUT_MIN_HEIGHT - typographyScale.textSm.lineHeight) / 2;
const ATTACHMENT_ROW_HEIGHT = 48;
const WAVE_BAR_FACTORS = [0.45, 0.7, 1, 0.7, 0.45];

const VoiceWaveform = ({ audioLevel = 0, isLoading = false }: { audioLevel?: number; isLoading?: boolean }) => {
  const clampedAudioLevel = Math.max(0, Math.min(1, audioLevel));

  return (
    <View style={styles.voiceWaveform}>
      {WAVE_BAR_FACTORS.map((factor, index) => {
        const barHeight = Math.max(4, 4 + (isLoading ? 0.35 : clampedAudioLevel) * 16 * factor);

        if (isLoading) {
          return (
            <MotiView
              key={index}
              from={{ height: barHeight, opacity: 0.45 }}
              animate={{ height: barHeight + 8 * factor, opacity: 1 }}
              transition={{
                type: "timing",
                duration: 520,
                delay: index * 80,
                loop: true,
                repeatReverse: true,
              }}
              style={styles.voiceWaveBar}
            />
          );
        }

        return <View key={index} style={[styles.voiceWaveBar, { height: barHeight }]} />;
      })}
    </View>
  );
};

const ChatComposerBar: React.FC<ChatComposerBarProps> = ({
  value,
  voiceInputPhase,
  voiceInputErrorMessage,
  audioLevel = 0,
  isAudioOn,
  placeholder,
  inputAccessibilityLabel,
  inputRef,
  onChangeText,
  onSubmitEditing,
  isDisabled,
  onRecordingStartPress,
  onRecordingCancelPress,
  onRecordingStopPress,
  onAudioOnPress,
  onAudioOffPress,
  chatContext,
  tools,
  activeToolId,
  onToolSelect,
  mathModeEnabled,
  onMathModeToggle,
  playbackSpeed,
  onPlaybackSpeedChange,
  isAttachmentEnabled,
  attachment,
  isAttachmentLoading,
  onAttachmentPress,
  onAddTaskPress,
  onAddAssignmentPress,
  onDeleteAttachmentPress,
  suggestedPrompts,
  onSuggestionPress,
}) => {
  const { t } = useTranslation();
  const { isMedium } = useResponsiveScreen();
  const { screenReaderIsEnabled } = useAccessibilityContext();
  const vkContext = useMathVirtualKeyboardContext();
  const virtualKeyboardVisible = vkContext?.virtualKeyboardVisible ?? false;
  const onToggleVirtualKeyboard = vkContext?.toggleVirtualKeyboard;
  const [isFocused, setIsFocused] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputShellRef = useRef<View>(null);
  const [inputHeight, setInputHeight] = useState(INPUT_MIN_HEIGHT);
  const [mathFieldValue, setMathFieldValue] = useState("");
  const mathFieldRef = useRef<MathFieldInputHandle>(null);
  const isRecording = voiceInputPhase === "recording";
  const isTranscribing = voiceInputPhase === "transcribing";
  const isMathModeActive = mathModeEnabled && Platform.OS === "web";
  const isInputDisabled = isDisabled || isRecording || isTranscribing || virtualKeyboardVisible;
  const areSecondaryControlsDisabled = isDisabled || isRecording || isTranscribing;
  const isMathFieldDisabled = isDisabled || isRecording || isTranscribing;

  const handleInputShellFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsFocused(true);
  }, []);

  const handleInputShellBlur = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    blurTimeoutRef.current = setTimeout(() => {
      blurTimeoutRef.current = null;
      if (Platform.OS === "web" && typeof document !== "undefined" && document.activeElement && inputShellRef.current) {
        const el = inputShellRef.current as unknown as HTMLElement;
        if (typeof el.contains === "function" && el.contains(document.activeElement)) {
          return;
        }
      }
      setIsFocused(false);
    }, 0);
  }, []);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (value === "") {
      setInputHeight(INPUT_MIN_HEIGHT);
    }
  }, [value]);

  useEffect(() => {
    if (virtualKeyboardVisible && Platform.OS === "web") {
      mathFieldRef.current?.focus();
      inputRef?.current?.blur();
    }
  }, [inputRef, virtualKeyboardVisible]);

  const handleChangeText = useCallback(
    (text: string) => {
      onChangeText(text);
    },
    [onChangeText]
  );

  const handleSubmit = useCallback(() => {
    const text = value.trim();
    const latex = isMathModeActive && mathFieldRef.current ? mathFieldRef.current.getValue().trim() : "";

    if (!text && !latex) {
      return;
    }

    const parts: string[] = [];
    if (text) {
      parts.push(text);
    }
    if (latex) {
      parts.push(`\\(${latex}\\)`);
    }

    if (latex) {
      mathFieldRef.current?.setValue("");
      setMathFieldValue("");
    }

    onSubmitEditing(parts.join("\n"));

    if (isMathModeActive) {
      requestAnimationFrame(() => {
        try {
          const keyboard = (
            window as Window & {
              mathVirtualKeyboard?: {
                hide: (options?: { animate?: boolean }) => void;
                visible: boolean;
              };
            }
          ).mathVirtualKeyboard;
          keyboard?.hide?.({ animate: true });
        } catch (err) {
          console.error("Failed to hide math virtual keyboard", err);
        }
        vkContext?.setVirtualKeyboardVisible?.(false);
        mathFieldRef.current?.focus();
      });
    }
  }, [isMathModeActive, onSubmitEditing, value, vkContext]);

  const handleInsertLatex = useCallback((latex: string) => {
    mathFieldRef.current?.insertLatex(latex);
  }, []);

  const handleToggleVirtualKeyboard = useCallback(() => {
    if (Platform.OS !== "web") {
      return;
    }

    if (onToggleVirtualKeyboard) {
      mathFieldRef.current?.focus();
      onToggleVirtualKeyboard();
      return;
    }

    try {
      const keyboard = (
        window as Window & {
          mathVirtualKeyboard?: {
            hide: () => void;
            show: () => void;
            visible: boolean;
          };
        }
      ).mathVirtualKeyboard;

      if (!keyboard) {
        return;
      }

      if (keyboard.visible) {
        keyboard.hide();
      } else {
        keyboard.show();
        mathFieldRef.current?.focus();
      }
    } catch {
      // mathVirtualKeyboard may not be initialized yet
    }
  }, [onToggleVirtualKeyboard]);

  const handleKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (isInputDisabled || screenReaderIsEnabled) {
      return;
    }

    const keyEvent = event.nativeEvent as TextInputKeyPressEventData & { shiftKey?: boolean };
    if (keyEvent.key === "Enter" && !keyEvent.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleContentSizeChange = (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
    const contentHeight = event.nativeEvent.contentSize?.height ?? INPUT_MIN_HEIGHT;
    const nextHeight = Math.min(INPUT_MAX_HEIGHT, Math.max(INPUT_MIN_HEIGHT, contentHeight));

    setInputHeight(nextHeight);
  };

  const hasTextContent = value.trim().length > 0;
  const hasMathContent = isMathModeActive ? mathFieldValue.trim().length > 0 : false;
  const hasMessage = hasTextContent || hasMathContent;
  const effectivePlaceholder = !hasMessage && voiceInputErrorMessage ? voiceInputErrorMessage : placeholder;
  const placeholderTextColor = voiceInputErrorMessage ? colors.error[500] : colors.grey[500];
  const actionAccessibilityLabel = isTranscribing
    ? t("Transcribing audio")
    : isRecording
      ? t("Stop voice input")
      : hasMessage
        ? t("Send message")
        : t("Start voice input");
  const actionAccessibilityHint = isRecording
    ? t("Press to stop recording")
    : hasMessage
      ? t("Press to send your message")
      : undefined;
  const cancelAccessibilityLabel = t("Cancel voice input");

  const handleActionPress = () => {
    if (isTranscribing) {
      return;
    }

    if (isRecording) {
      onRecordingStopPress();
      return;
    }

    if (hasMessage) {
      handleSubmit();
      return;
    }

    onRecordingStartPress();
  };

  return (
    <View style={styles.wrapper}>
      {suggestedPrompts && suggestedPrompts.length > 0 && onSuggestionPress && (
        <View style={styles.suggestedPromptsContainer}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.suggestedPromptsContent}>
            {suggestedPrompts.map((prompt, index) => (
              <Pressable
                key={index}
                style={({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
                  styles.promptItem,
                  hovered && !areSecondaryControlsDisabled && styles.promptItemHovered,
                  pressed && styles.promptItemPressed,
                ]}
                onPress={() => !areSecondaryControlsDisabled && onSuggestionPress(prompt.text)}
                role="button"
                accessibilityLabel={prompt.text}
                accessibilityHint="Select this prompt to use it in your message">
                <CustomText
                  size="xs"
                  useTranslationText={false}
                  text={prompt.text}
                  style={styles.promptText}
                  numberOfLines={1}
                />
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
      <View style={styles.content}>
        <View
          ref={inputShellRef}
          nativeID="chat-composer-shell"
          style={[styles.inputShell, isFocused && styles.inputShellFocused]}>
          {attachment ? (
            <View style={styles.overflowHidden}>
              <ScrollView horizontal style={styles.attachedItemsScrollViewWrapper}>
                <View style={styles.attachedItemsWrapper}>
                  <AttachmentChip
                    type={attachment.type}
                    name={attachment.name}
                    duedate={attachment.duedate}
                    isLoading={attachment.isLoading ?? isAttachmentLoading}
                    removeChip={onDeleteAttachmentPress}
                  />
                </View>
              </ScrollView>
            </View>
          ) : null}

          <Tooltip
            content={virtualKeyboardVisible ? "Disable virtual keyboard to type here" : undefined}
            placement="top"
            showArrow={false}>
            <View
              style={[styles.inputTopRow, isMedium ? styles.inputTopRowDesktop : styles.inputTopRowMobile]}
              pointerEvents="auto">
              <TextInput
                ref={inputRef}
                value={value}
                maxLength={7500}
                multiline
                numberOfLines={1}
                placeholder={effectivePlaceholder}
                placeholderTextColor={placeholderTextColor}
                style={[styles.textInput, { minHeight: inputHeight, maxHeight: INPUT_MAX_HEIGHT }]}
                onChangeText={handleChangeText}
                onSubmitEditing={() => handleSubmit()}
                onKeyPress={handleKeyPress}
                onContentSizeChange={handleContentSizeChange}
                onFocus={handleInputShellFocus}
                onBlur={handleInputShellBlur}
                scrollEnabled={inputHeight >= INPUT_MAX_HEIGHT}
                editable={!isInputDisabled}
                pointerEvents={virtualKeyboardVisible ? "none" : undefined}
                aria-label={inputAccessibilityLabel ?? "Chat text input"}
                accessibilityLabel={inputAccessibilityLabel ?? "Chat text input"}
                accessibilityHint={
                  virtualKeyboardVisible ? "Disable virtual keyboard to type here" : "Enter your message here"
                }
              />
            </View>
          </Tooltip>

          {isMathModeActive && (
            <View style={styles.equationSection}>
              <View style={styles.equationSeparator} />
              <CustomText
                text="Equation"
                useTranslationText={false}
                style={[styles.equationLabel, isMedium ? styles.equationLabelDesktop : styles.equationLabelMobile]}
              />
              <MathFieldInput
                ref={mathFieldRef}
                onValueChange={setMathFieldValue}
                onSubmit={handleSubmit}
                isDisabled={isMathFieldDisabled}
                onFocus={handleInputShellFocus}
                onBlur={handleInputShellBlur}
              />
              <MathKeyboardPalette
                onInsert={handleInsertLatex}
                onToggleVirtualKeyboard={handleToggleVirtualKeyboard}
                isVirtualKeyboardOpen={virtualKeyboardVisible}
              />
            </View>
          )}

          <View style={[styles.controlsRow, isMedium ? styles.controlsRowDesktop : styles.controlsRowMobile]}>
            <View style={styles.leftControls}>
              {isAttachmentEnabled ? (
                <View style={(isAttachmentLoading || areSecondaryControlsDisabled) && styles.iconButtonDisabled}>
                  <AddToMessage
                    onAddAttachment={
                      isAttachmentLoading || areSecondaryControlsDisabled ? undefined : onAttachmentPress
                    }
                    onAddTask={isAttachmentLoading || areSecondaryControlsDisabled ? undefined : onAddTaskPress}
                    onAddAssignment={
                      isAttachmentLoading || areSecondaryControlsDisabled ? undefined : onAddAssignmentPress
                    }
                  />
                </View>
              ) : null}

              <ToolsMenu
                chatContext={chatContext}
                isDisabled={areSecondaryControlsDisabled}
                tools={tools}
                activeToolId={activeToolId}
                onToolSelect={onToolSelect}
                mathModeEnabled={mathModeEnabled}
                onMathModeToggle={onMathModeToggle}
              />
              <VoiceMenu
                isDisabled={areSecondaryControlsDisabled}
                isAudioOn={isAudioOn}
                onAudioOnPress={onAudioOnPress}
                onAudioOffPress={onAudioOffPress}
                playbackSpeed={playbackSpeed}
                onPlaybackSpeedChange={onPlaybackSpeedChange}
              />
            </View>

            <View style={styles.rightControls}>
              {isRecording && (
                <Tooltip content={cancelAccessibilityLabel} offset={6} horizontalOffset={-8}>
                  <Pressable
                    onPress={onRecordingCancelPress}
                    role="button"
                    accessibilityLabel={cancelAccessibilityLabel}
                    style={(state) => {
                      const hovered = (state as { hovered?: boolean }).hovered;
                      return [
                        styles.cancelActionButton,
                        hovered && styles.cancelActionButtonHovered,
                        state.pressed && styles.flatIconButtonPressed,
                      ];
                    }}>
                    <X size={18} color={colors.grey[400]} />
                  </Pressable>
                </Tooltip>
              )}
              {(isRecording || isTranscribing) && (
                <View style={styles.voiceIndicator}>
                  <VoiceWaveform audioLevel={audioLevel} isLoading={isTranscribing} />
                </View>
              )}
              <Tooltip content={actionAccessibilityLabel} offset={6}>
                <Pressable
                  onPress={handleActionPress}
                  disabled={isTranscribing || (isDisabled && !isRecording)}
                  role="button"
                  accessibilityLabel={actionAccessibilityLabel}
                  accessibilityHint={actionAccessibilityHint}
                  style={(state) => {
                    const hovered = (state as { hovered?: boolean }).hovered;
                    return [
                      styles.ghostActionButton,
                      !isTranscribing && !isDisabled && isRecording && styles.recordingActionButton,
                      !isTranscribing && !isDisabled && hovered && isRecording && styles.recordingActionButtonHovered,
                      !isTranscribing &&
                        !isDisabled &&
                        hovered &&
                        !isRecording &&
                        hasMessage &&
                        styles.sendActionButtonHovered,
                      !isTranscribing &&
                        !isDisabled &&
                        hovered &&
                        !isRecording &&
                        !hasMessage &&
                        styles.ghostActionButtonHovered,
                      !isTranscribing && !isDisabled && state.pressed && styles.flatIconButtonPressed,
                      (isTranscribing || isDisabled) && styles.iconButtonDisabled,
                    ];
                  }}>
                  {(state) => {
                    const hovered = (state as { hovered?: boolean }).hovered;
                    const isHovered = Boolean(hovered) && !isDisabled && !isTranscribing;
                    const micIconColor = isHovered ? colors.grey[600] : colors.grey[400];

                    return (
                      <AnimatePresence initial={false} exitBeforeEnter>
                        <MotiView
                          key={isRecording ? "stop" : hasMessage ? "send" : "mic"}
                          from={{ opacity: 0, scale: 0.6 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.6 }}
                          transition={{ type: "timing", duration: 100 }}
                          pointerEvents="none"
                          style={styles.actionIcon}>
                          {isRecording ? (
                            <Square size={14} color={colors.brand[600]} fill={colors.brand[600]} />
                          ) : hasMessage ? (
                            <Send size={18} color={colors.brand[600]} />
                          ) : (
                            <MicrophoneIcon size={18} color={micIconColor} weight="fill" />
                          )}
                        </MotiView>
                      </AnimatePresence>
                    );
                  }}
                </Pressable>
              </Tooltip>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: "relative",
    bottom: "auto",
    left: "auto",
    right: "auto",
    zIndex: 30,
  },
  content: {
    width: "100%",
    maxWidth: 768,
    alignSelf: "center",
    paddingHorizontal: spacing.xs,
  },
  inputShell: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.xl,
    marginBottom: spacing.md,
    overflow: "visible",
    ...webTransitions.fast,
  },
  inputShellFocused: {
    borderColor: colors.brand[100],
    ...(Platform.OS === "web" ? { boxShadow: `0 0 0 2px ${colors.brand[100]}` } : {}),
  },
  inputTopRow: {
    width: "100%",
  },
  inputTopRowDesktop: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.xs,
  },
  inputTopRowMobile: {
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.xxs,
  },
  textInput: {
    ...typographyStyles.textSm,
    flex: 1,
    minHeight: INPUT_MIN_HEIGHT,
    color: colors.grey[900],
    paddingVertical: INPUT_VERTICAL_PADDING,
    paddingHorizontal: 0,
    textAlignVertical: "center",
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
    minWidth: 0,
  },
  voiceIndicator: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 36,
  },
  voiceWaveform: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    minHeight: 18,
  },
  voiceWaveBar: {
    width: 3,
    borderRadius: radii.full,
    backgroundColor: colors.grey[400],
  },
  controlsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  controlsRowDesktop: {
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.xxs,
    paddingBottom: spacing.sm,
  },
  controlsRowMobile: {
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.xxs,
    paddingBottom: spacing.xs,
  },
  leftControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  rightControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  flatIconButtonPressed: {
    opacity: 0.8,
  },
  ghostActionButton: {
    width: 36,
    height: 36,
    borderRadius: radii.lg,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  cancelActionButton: {
    width: 36,
    height: 36,
    borderRadius: radii.full,
    backgroundColor: colors.white,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  cancelActionButtonHovered: {
    backgroundColor: colors.grey[50],
  },
  actionIcon: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
  },
  recordingActionButton: {
    backgroundColor: colors.brand[50],
  },
  recordingActionButtonHovered: {
    backgroundColor: colors.brand[100],
  },
  ghostActionButtonHovered: {
    backgroundColor: colors.grey[200],
  },
  sendActionButtonHovered: {
    backgroundColor: colors.brand[50],
  },
  iconButtonDisabled: {
    opacity: 0.5,
  },
  overflowHidden: {
    alignSelf: "stretch",
    overflow: "hidden",
  },
  attachedItemsScrollViewWrapper: {
    padding: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    minHeight: ATTACHMENT_ROW_HEIGHT,
  },
  attachedItemsWrapper: {
    flexDirection: "row",
    gap: spacing.xs,
    alignItems: "center",
  },
  equationSection: {
    width: "100%",
  },
  equationSeparator: {
    height: 1,
    backgroundColor: colors.grey[200],
    marginHorizontal: spacing.md,
  },
  equationLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
    color: colors.grey[400],
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  equationLabelDesktop: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  equationLabelMobile: {
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.xs,
  },
  suggestedPromptsContainer: {
    alignSelf: "stretch",
    overflow: "hidden",
    paddingBottom: spacing.xs,
  },
  suggestedPromptsContent: {
    flexDirection: "row",
    gap: spacing.xs,
    paddingHorizontal: spacing.xs,
    justifyContent: "center",
    flexGrow: 1,
  },
  promptItem: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: radii.full,
    backgroundColor: colors.grey[200],
    ...webTransitions.fast,
  },
  promptItemHovered: {
    backgroundColor: colors.grey[300],
    transform: [{ scale: 1.015 }],
  },
  promptItemPressed: {
    opacity: 0.9,
    transform: [{ scale: 0.985 }],
  },
  promptText: {
    ...typographyStyles.textXs,
    color: semanticColors.typography.textLight,
    fontWeight: "500",
  },
});

export default ChatComposerBar;
